import React, { useState, useEffect } from 'react';
import { 
  Info, 
  ChevronDown, 
  X, 
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Check
} from 'lucide-react';
import billingService from '../../services/billingServices';
import styles from './BillingSettings.module.css';

export default function BillingSettings() {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBillingInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 Starting to fetch billing info...');
        
        // Check for success/cancel parameters in URL
        const urlParams = new URLSearchParams(window.location.search);
        const success = urlParams.get('success');
        const canceled = urlParams.get('canceled');
        
        if (success === 'true') {
          console.log('✅ Subscription successful! Refreshing billing data...');
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        } else if (canceled === 'true') {
          console.log('❌ Subscription canceled by user');
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        
        // Fetch current subscription
        const subscription = await billingService.getCurrentSubscription();
        console.log('🔍 Subscription data received:', subscription);
        console.log('🔍 Subscription status:', subscription?.status);
        console.log('🔍 Subscription plan_name:', subscription?.plan_name);
        console.log('🔍 Subscription price:', subscription?.price);
        console.log('🔍 Subscription is_paid:', subscription?.is_paid);
        setCurrentPlan(subscription);
        
        // Fetch billing history
        const history = await billingService.getBillingHistory();
        setBillingHistory(Array.isArray(history) ? history : []);
        
        // Fetch available plans
        console.log('🔍 About to fetch available plans...');
        const plans = await billingService.getAvailablePlans();
        console.log('🔍 Available plans from API:', plans);
        
        // If API fails, use fallback data that matches the API structure
        if (!Array.isArray(plans) || plans.length === 0) {
          console.log('🔍 API returned no plans, using fallback data');
          const fallbackPlans = [
            {
              id: 'pro',
              name: 'Pro',
              price: 50.00,
              interval: 'month',
              features: ['Unlimited projects', 'Metered 2D/3D CAD servers', 'Metered Agent requests', 'Advanced usage analytics']
            },
            {
              id: 'business',
              name: 'Business',
              price: 200.00,
              interval: 'month',
              features: ['Everything in Pro', 'Extended limits on 2D/3D CAD servers', 'Extended limits on Agent requests', 'Advanced project analytics', 'Multi-user access', 'Productivity tools integrations']
            },
            {
              id: 'enterprise',
              name: 'Enterprise',
              price: 'Custom',
              interval: 'month',
              features: ['Everything in Business', '10x usage on 2D/3D CAD servers', '10x usage on Agent requests', 'On-premise installations', 'Custom integrations', 'Access control features', 'Priority support and account management']
            }
          ];
          setAvailablePlans(fallbackPlans);
        } else {
          setAvailablePlans(plans);
        }
        
      } catch (err) {
        console.error('Failed to fetch billing info:', err);
        setError(err.message || 'Failed to load billing information');
        // Set defaults to prevent UI crashes
        setCurrentPlan(null);
        setBillingHistory([]);
        setAvailablePlans([]);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure authentication is fully established
    const timeoutId = setTimeout(fetchBillingInfo, 100);
    
    return () => clearTimeout(timeoutId);
    
    // Check for URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('already_on_plan') === 'true') {
      alert('You are already on this plan!');
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);



  const handleContactSales = () => {
    // Open email client or contact form
    const subject = encodeURIComponent('Enterprise Plan Inquiry');
    const body = encodeURIComponent('Hi,\n\nI am interested in learning more about the Enterprise plan for Foundry. Please contact me to discuss pricing and features.\n\nThank you!');
    window.open(`mailto:support@siameseai.com?subject=${subject}&body=${body}`, '_blank');
  };

  const handleManageSubscription = async (planId = null) => {
    try {
      // If we have a planId, this is a new subscription - use checkout
      if (planId) {
        console.log('🔄 Creating checkout session for plan:', planId);
        const checkoutUrl = await billingService.createCheckoutSession(planId);
        console.log('🔄 Checkout URL received:', checkoutUrl);
        
        if (checkoutUrl) {
          console.log('✅ Redirecting to Stripe Checkout...');
          window.location.href = checkoutUrl;
        } else {
          console.error('❌ No checkout URL received from API');
          setError('Failed to create checkout session - no URL received');
        }
      } else {
        // This is for existing subscriptions - use customer portal
        console.log('🔄 Opening subscription management portal...');
        const returnUrl = window.location.origin + '/settings/billing';
        console.log('🔄 Return URL:', returnUrl);
        
        const portalUrl = await billingService.createCustomerPortalSession(returnUrl);
        console.log('🔄 Portal URL received:', portalUrl);
        
        if (portalUrl) {
          console.log('✅ Redirecting to Stripe Customer Portal...');
          window.location.href = portalUrl;
        } else {
          console.error('❌ No portal URL received from API');
          setError('Failed to open subscription management page - no portal URL received');
        }
      }
    } catch (err) {
      console.error('❌ Failed to manage subscription:', err);
      setError(`Failed to manage subscription: ${err.message || 'Unknown error'}`);
    }
  };





  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading billing information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className={styles.billingSettings}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1>Billing & Subscription</h1>
          <p className={styles.subtitle}>Manage your subscription and billing information</p>
        </div>
      </div>

      {/* Personal Account Plan Section */}
      <div className={styles.settingsSection}>
        <h2>Your account plan</h2>
        <div className={styles.planInfo}>
          {currentPlan && (currentPlan.status === 'active' || currentPlan.status === 'trialing' || currentPlan.is_paid) ? (
            <>
              <div className={styles.currentPlan}>
                <div className={styles.planDetails}>
                <div className={styles.planName}>{currentPlan.plan_name || 'Unknown Plan'}</div>
                <div className={styles.planPrice}>
                    ${currentPlan.price || 0}/user/month
                  </div>
                </div>
                <div className={styles.planStatusContainer}>
                  <span className={`${styles.statusBadge} ${currentPlan.status === 'trialing' ? styles.statusTrialing : styles.statusActive}`}>
                    {currentPlan.status === 'trialing' ? 'TRIAL' : 'ACTIVE'}
                  </span>
                  {currentPlan.next_billing_date && (
                    <span className={styles.nextBilling}>
                      {currentPlan.status === 'trialing' ? 'Trial ends' : 'Next billing'}: {new Date(currentPlan.next_billing_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              
            </>
          ) : (
            <div className={styles.noPlan}>
              <p>No active subscription</p>
            </div>
          )}
        </div>
      </div>

      {/* Manage your plan - Major Section */}
      <div className={styles.settingsSection}>
        <h2>Manage your plan</h2>
        <div className={styles.planCards}>
          {loading ? (
            <div className={styles.loadingMessage}>Loading plans...</div>
          ) : availablePlans && availablePlans.length > 0 ? (
            availablePlans.map((plan) => {
            // Debug logging for plan matching
            console.log(`🔍 Checking plan: ${plan.name} against currentPlan:`, currentPlan);
            
            const isCurrentPlan = currentPlan && currentPlan.plan_name && 
              (currentPlan.plan_name.toLowerCase().includes(plan.name.toLowerCase()) ||
               (plan.name === 'Pro' && currentPlan.plan_name === 'Pro Plan') ||
               (plan.name === 'Business' && currentPlan.plan_name === 'Business Plan') ||
               (plan.name === 'Enterprise' && currentPlan.plan_name === 'Enterprise Plan') ||
               (currentPlan.plan_name.toLowerCase() === plan.name.toLowerCase()));
            
            console.log(`🔍 Is ${plan.name} current plan?`, isCurrentPlan);
            
            // Determine button text and styling
            let actionText = 'Manage';
            let buttonClass = styles.subscribeButton;
            let isUpgrade = false;
            let isDowngrade = false;
            
            if (isCurrentPlan) {
              actionText = 'Cancel';
              buttonClass = `${styles.subscribeButton} ${styles.cancelButton}`;
            } else if (plan.name === 'Enterprise') {
              actionText = 'Contact Sales';
              buttonClass = styles.subscribeButton;
            } else if (currentPlan && currentPlan.price && plan.price) {
              const currentPrice = typeof currentPlan.price === 'number' ? currentPlan.price : parseFloat(currentPlan.price);
              const planPrice = typeof plan.price === 'number' ? plan.price : parseFloat(plan.price);
              isUpgrade = planPrice > currentPrice;
              isDowngrade = planPrice < currentPrice;
              actionText = isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Change Plan';
              buttonClass = isUpgrade ? `${styles.subscribeButton} ${styles.upgradeButton}` : 
                           isDowngrade ? `${styles.subscribeButton} ${styles.downgradeButton}` : 
                           styles.subscribeButton;
            } else {
              actionText = 'Subscribe';
              buttonClass = styles.subscribeButton;
            }
            
            return (
              <div key={plan.id} className={`${styles.planCard} ${isCurrentPlan ? styles.currentPlanCard : ''}`}>
                <div className={styles.planHeader}>
                  <h4 className={styles.planName}>{plan.name}</h4>
                  <div className={styles.planPrice}>
                    {typeof plan.price === 'number' ? (
                      <>
                        <span className={styles.priceAmount}>${plan.price}</span>
                      <span className={styles.priceInterval}>/user/month</span>
                      </>
                    ) : (
                      <span className={styles.priceAmount}>{plan.price}</span>
                    )}
                  </div>
                  {isCurrentPlan && (
                    <div className={styles.currentPlanBanner}>
                      Current Plan
                    </div>
                  )}
                  {!isCurrentPlan && currentPlan && currentPlan.price && plan.price && typeof plan.price === 'number' && (
                    <div className={styles.priceComparison}>
                      {isUpgrade && (
                        <span className={styles.priceIncrease}>
                          +${(plan.price - (typeof currentPlan.price === 'number' ? currentPlan.price : parseFloat(currentPlan.price))).toFixed(2)}/month
                        </span>
                      )}
                      {isDowngrade && (
                        <span className={styles.priceDecrease}>
                          -${((typeof currentPlan.price === 'number' ? currentPlan.price : parseFloat(currentPlan.price)) - plan.price).toFixed(2)}/month
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className={styles.planFeatures}>
                  {plan.features.map((feature, index) => (
                    <div key={index} className={styles.feature}>
                      <CheckCircle className={styles.featureIcon} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <div className={styles.planActions}>
                  {actionText === 'Contact Sales' ? (
                    <button 
                      className={buttonClass}
                      onClick={() => handleContactSales()}
                    >
                      {actionText}
                    </button>
                  ) : (
                    <button 
                      className={buttonClass}
                      onClick={() => {
                        if (actionText === 'Subscribe') {
                          handleManageSubscription(plan.id);
                        } else {
                          handleManageSubscription();
                        }
                      }}
                      disabled={loading}
                    >
                      {actionText === 'Cancel' && <X className={styles.buttonIcon} />}
                      {actionText === 'Upgrade' && <ArrowUp className={styles.buttonIcon} />}
                      {actionText === 'Downgrade' && <ArrowDown className={styles.buttonIcon} />}
                      {actionText}
                    </button>
                  )}
                </div>
              </div>
            );
            })
          ) : (
            <div className={styles.errorMessage}>No plans available. Please try again later.</div>
          )}
        </div>
      </div>

    </div>
  );
}
